import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, UserPlus, Search, Check, X, MessageCircle, Flame, UserMinus } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

export default function Friends() {
  const nav = useNavigate();
  const [tab, setTab] = useState("friends"); // friends | requests | search
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const loadFriends = async () => setFriends((await api.get("/friends")).data);
  const loadRequests = async () => setRequests((await api.get("/friends/requests")).data);
  useEffect(() => { loadFriends(); loadRequests(); }, []);

  const doSearch = async () => {
    if (!query.trim()) return setResults([]);
    const r = await api.get("/friends/search", { params: { q: query.trim() } });
    setResults(r.data);
  };

  const sendRequest = async (uid) => {
    try {
      await api.post("/friends/request", { user_id: uid });
      toast.success("Request sent");
      doSearch(); loadRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const accept = async (rid) => {
    await api.post("/friends/accept", { request_id: rid });
    toast.success("Friend added"); loadFriends(); loadRequests();
  };
  const reject = async (rid) => {
    await api.post("/friends/reject", { request_id: rid });
    loadRequests();
  };
  const cancel = async (rid) => {
    await api.post("/friends/cancel", { request_id: rid });
    loadRequests();
  };
  const remove = async (uid) => {
    if (!window.confirm("Remove friend?")) return;
    await api.delete(`/friends/${uid}`);
    loadFriends();
  };

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="friends-back">
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="ps-heading text-3xl font-bold mt-4">Friends</h1>

        <div className="mt-5 flex gap-2 bg-[#0e1d2a] border border-[#1a2f40] rounded-full p-1">
          {[
            { k: "friends", l: `Friends ${friends.length ? `· ${friends.length}` : ""}` },
            { k: "requests", l: `Requests ${requests.incoming.length ? `· ${requests.incoming.length}` : ""}` },
            { k: "search", l: "Find" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`friends-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex-1 py-2 rounded-full text-xs font-semibold ${tab === t.k ? "bg-[#2fbf71] text-[#0a1822]" : "text-slate-400"}`}
            >{t.l}</button>
          ))}
        </div>

        {tab === "friends" && (
          <div className="mt-5 space-y-2">
            {friends.length === 0 && <Empty text="No friends yet. Tap Find to add some." />}
            {friends.map((f) => (
              <div key={f.id} className="ps-card p-4 flex items-center gap-3" data-testid={`friend-${f.id}`}>
                <Avatar name={f.name} />
                <div className="flex-1">
                  <div className="font-semibold">{f.name}</div>
                  <div className="text-xs text-slate-400 inline-flex items-center gap-1">
                    <Flame size={10} color="#F59E0B" /> {f.streak}d · {f.points} pts
                  </div>
                </div>
                <Link
                  to={`/messages/${f.id}`}
                  data-testid={`friend-msg-${f.id}`}
                  className="p-2 rounded-full bg-[#142534] text-[#2fbf71]"
                  aria-label="Message"
                >
                  <MessageCircle size={16} />
                </Link>
                <button data-testid={`friend-remove-${f.id}`} onClick={() => remove(f.id)} className="p-2 rounded-full bg-[#142534] text-slate-400">
                  <UserMinus size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="mt-5 space-y-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Incoming</div>
              {requests.incoming.length === 0 && <Empty text="No incoming requests." compact />}
              {requests.incoming.map((r) => (
                <div key={r.id} className="ps-card p-4 flex items-center gap-3 mb-2" data-testid={`req-in-${r.id}`}>
                  <Avatar name={r.other_user?.name} />
                  <div className="flex-1">
                    <div className="font-semibold">{r.other_user?.name}</div>
                    <div className="text-xs text-slate-400">wants to connect</div>
                  </div>
                  <button data-testid={`req-accept-${r.id}`} onClick={() => accept(r.id)} className="p-2 rounded-full bg-[#2fbf71] text-[#0a1822]">
                    <Check size={14} />
                  </button>
                  <button data-testid={`req-reject-${r.id}`} onClick={() => reject(r.id)} className="p-2 rounded-full bg-[#142534] text-slate-400">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Outgoing</div>
              {requests.outgoing.length === 0 && <Empty text="No pending invites." compact />}
              {requests.outgoing.map((r) => (
                <div key={r.id} className="ps-card p-4 flex items-center gap-3 mb-2" data-testid={`req-out-${r.id}`}>
                  <Avatar name={r.other_user?.name} />
                  <div className="flex-1">
                    <div className="font-semibold">{r.other_user?.name}</div>
                    <div className="text-xs text-slate-400">request pending</div>
                  </div>
                  <button data-testid={`req-cancel-${r.id}`} onClick={() => cancel(r.id)} className="text-xs px-3 py-1.5 rounded-full border border-[#1a2f40] text-slate-300">
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "search" && (
          <div className="mt-5">
            <div className="flex gap-2">
              <input
                data-testid="friends-search-input"
                value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="Search name or email"
                className="flex-1 bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#2fbf71]"
              />
              <button data-testid="friends-search-btn" onClick={doSearch} className="ps-button-primary px-4 inline-flex items-center gap-1">
                <Search size={16} />
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {results.length === 0 && query && <Empty text="No matches. Try a different name." compact />}
              {results.map((u) => (
                <div key={u.id} className="ps-card p-4 flex items-center gap-3" data-testid={`search-result-${u.id}`}>
                  <Avatar name={u.name} />
                  <div className="flex-1">
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </div>
                  {u.is_friend ? (
                    <span className="text-xs text-[#2fbf71] font-semibold">Friends</span>
                  ) : u.request_state === "outgoing" ? (
                    <span className="text-xs text-slate-400">Sent</span>
                  ) : u.request_state === "incoming" ? (
                    <button data-testid={`search-accept-${u.id}`} onClick={() => accept(u.request_id)} className="ps-button-primary text-xs py-1.5 px-3">Accept</button>
                  ) : (
                    <button data-testid={`search-add-${u.id}`} onClick={() => sendRequest(u.id)} className="ps-button-primary text-xs py-1.5 px-3 inline-flex items-center gap-1">
                      <UserPlus size={12} /> Add
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}

function Avatar({ name }) {
  return (
    <div className="w-10 h-10 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center font-bold">
      {name?.[0] ?? "?"}
    </div>
  );
}
function Empty({ text, compact }) {
  return <div className={`text-slate-500 text-sm ${compact ? "" : "py-10 text-center"}`}>{text}</div>;
}
